import { isDefined } from '@sgme/fp';
import type { CellClassParams, CellDoubleClickedEvent, CellStyleFunc, ColDef, ColTypeDef } from 'ag-grid-community';
import { useStatusColumnType, useYesNoColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { useUserNotification } from 'hooks/useUserNotification';
import { useCallback, useMemo, useState } from 'react';
import type { SecondaryMarketProduct } from 'state/secondaryMarket/productSearch';
import {
  limitOrderEnabledByRtTradeAuthorisation,
  limitOrderEnabledByStatus,
} from 'state/secondaryMarket/productSearch/productSearch.utils';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { TranslatableContent } from 'utils/intl';

import type { PriceTypeInfo } from '../../../shared/Blotter/Grid/Hooks/usePercentOrAmountColumnType';
import { usePercentOrAmountColumnType } from '../../../shared/Blotter/Grid/Hooks/usePercentOrAmountColumnType';

type ModalState = [
  {
    hasSelection: boolean;
    selectedRow: SecondaryMarketProduct | null | undefined;
  },
  {
    onUnselect: () => void;
    onSelectedRow: (data: SecondaryMarketProduct) => void;
  },
];

export function makeClickableCellStyleWithPredicate<TData extends {}>(
  isCellClickable: (data: TData) => boolean,
): CellStyleFunc<TData> {
  return (params: CellClassParams<TData>) => {
    const data = params.data;
    const canClick = data !== undefined && isCellClickable(data);
    return canClick ? { cursor: 'pointer' } : null;
  };
}

export function useModalState(): ModalState {
  const [selectedRow, setSelectedRow] = useState<SecondaryMarketProduct | null>();

  const onUnselect = useCallback(() => {
    setSelectedRow(null);
  }, []);

  const onSelectedRow = useCallback((data: SecondaryMarketProduct) => {
    setSelectedRow(data);
  }, []);

  return [
    { hasSelection: isDefined(selectedRow), selectedRow },
    { onSelectedRow, onUnselect },
  ];
}

export function useBidAskColumnType(
  getPriceTypeInfo: (data: SecondaryMarketProduct) => PriceTypeInfo,
  isRedirectEnabled: (data: SecondaryMarketProduct, type: 'bid' | 'ask') => boolean,
  onDoubleClicked: (rowId: string, tradeWay: TradeWay) => void,
): ColTypeDef {
  const percentOrAmountColumn = usePercentOrAmountColumnType(getPriceTypeInfo);

  const getBidOrdAsk = useCallback((colDef: ColDef) => {
    if (colDef.field?.toString().toLowerCase().includes('bid')) {
      return 'bid';
    }
    if (colDef.field?.toString().toLowerCase().includes('ask')) {
      return 'ask';
    }
    return undefined;
  }, []);

  const onCellDoubleClicked = useCallback(
    (event: CellDoubleClickedEvent) => {
      const bidOrdAsk = getBidOrdAsk(event.colDef);
      if (!bidOrdAsk) {
        return;
      }

      if (isRedirectEnabled(event.data, bidOrdAsk)) {
        const type = bidOrdAsk === 'ask' ? 'ClientBuy' : 'ClientSell';
        onDoubleClicked(event.data.rowId, type);
      }
    },
    [getBidOrdAsk, isRedirectEnabled, onDoubleClicked],
  );

  const cellStyle = useCallback(
    (params: CellClassParams) => {
      const bidOrdAsk = getBidOrdAsk(params.colDef);
      if (!bidOrdAsk) {
        return;
      }

      if (isRedirectEnabled(params.data, bidOrdAsk)) {
        return { cursor: 'pointer' };
      }
    },
    [getBidOrdAsk, isRedirectEnabled],
  );

  return useMemo(
    () => ({ ...percentOrAmountColumn, onCellDoubleClicked: onCellDoubleClicked, cellStyle: cellStyle }),
    [cellStyle, onCellDoubleClicked, percentOrAmountColumn],
  );
}

export function getLimitOrderStatusColor(value: boolean | undefined | null) {
  switch (value) {
    case true:
      return 'success';
    case false:
      return 'danger';
    default:
      return undefined;
  }
}

export const useLimitOrderStatusColumnType = (onOpenLimitOrderTab: (rowId: string) => void) => {
  const notifications = useUserNotification();
  const textFormatter = useCallback((val: boolean | undefined | null) => {
    if (val === true) {
      return 'Yes';
    } else if (val === false) {
      return 'No';
    }
    return '';
  }, []);
  const onCellDoubleClicked = useCallback(
    ({ data }: CellDoubleClickedEvent<SecondaryMarketProduct>) => {
      if (!data) {
        return;
      }
      const title: TranslatableContent = {
        id: 'secondaryMarket.products.notifications.title.limitOrderNotAvailable',
      };
      if (!limitOrderEnabledByStatus(data)) {
        notifications.warn({
          title,
          content: [{ id: 'secondaryMarket.products.notifications.message.limitStatusOff' }],
        });
      } else if (!limitOrderEnabledByRtTradeAuthorisation(data)) {
        notifications.warn({
          title,
          content: [{ id: 'secondaryMarket.products.notifications.message.rtStatusOff' }],
          minWidth: 300,
        });
      } else {
        onOpenLimitOrderTab(data.rowId);
      }
    },
    [notifications, onOpenLimitOrderTab],
  );
  const statusColumnType = useStatusColumnType(getLimitOrderStatusColor, false, false, textFormatter);
  return useMemo(() => {
    return {
      ...statusColumnType,
      onCellDoubleClicked,
      cellStyle: makeClickableCellStyleWithPredicate<SecondaryMarketProduct>(
        p => limitOrderEnabledByStatus(p) && limitOrderEnabledByRtTradeAuthorisation(p),
      ),
    };
  }, [onCellDoubleClicked, statusColumnType]);
};

export function useHasDlpSecondaryColumn(onOpenDlpProduct: (isin: string) => void) {
  const yesNoColumn = useYesNoColumnType();
  return useMemo(() => {
    return {
      ...yesNoColumn,
      onCellDoubleClicked: (evt: CellDoubleClickedEvent<SecondaryMarketProduct>) => {
        if (evt.data?.hasDlpSecondaryMarket && evt.data.isin) {
          onOpenDlpProduct(evt.data.isin);
        }
      },
      cellStyle: makeClickableCellStyleWithPredicate<SecondaryMarketProduct>(p => p.hasDlpSecondaryMarket ?? false),
    };
  }, [onOpenDlpProduct, yesNoColumn]);
}
